/**
 * The navbar's light and dark switch.
 *
 * It sets an explicit preference, which is deliberate: the three-state control
 * that can hand the choice back to the operating system lives in Settings
 * (FR-056). The icon shows where the switch will take you, not where you are.
 *
 * @author John Grimes
 */

import { MoonIcon, SunIcon } from "@primer/octicons-react";

import { useTheme } from "../lib/theme";

import type { JSX } from "react";

/** Props for {@link ThemeToggle}. */
export type ThemeToggleProps = {
  /**
   * Which side the tooltip opens on. Beneath by default; to the left when
   * the switch is the last control in the bar, where a tooltip centred
   * beneath it would run off the edge of the screen.
   */
  tooltipSide?: "bottom" | "left";
};

/**
 * Renders the theme switch.
 *
 * @param props - The component's props.
 * @param props.tooltipSide - Which side the tooltip opens on.
 * @returns The switch element.
 *
 * @example
 * ```tsx
 * <ThemeToggle tooltipSide="left" />
 * ```
 */
export const ThemeToggle = ({
  tooltipSide = "bottom",
}: Readonly<ThemeToggleProps>): JSX.Element => {
  const { resolvedTheme, setPreference } = useTheme();
  const nextTheme = resolvedTheme === "dark" ? "light" : "dark";
  const label = `Switch to the ${nextTheme} theme`;
  return (
    <div
      className={`tooltip ${tooltipSide === "left" ? "tooltip-left" : "tooltip-bottom"}`}
      data-tip={label}
    >
      <button
        type="button"
        className="btn btn-ghost btn-circle"
        aria-label={label}
        onClick={() => {
          setPreference(nextTheme);
        }}
      >
        {resolvedTheme === "dark" ? (
          <SunIcon size={15} aria-hidden />
        ) : (
          <MoonIcon size={15} aria-hidden />
        )}
      </button>
    </div>
  );
};
